import { useEffect, useState } from "react";
import { db } from "../firebaseConfig";
import { collection, getDocs, query, where, orderBy, limit } from "firebase/firestore";

interface RejectionData {
  id: string;
  taskTitle: string;
  taskCategory: string;
  reason: string;
  rejectedBy: string;
  rejectedAt: number;
  userId: string;
  similarity?: number;
  adminReviewedBy?: string;
  mediaUrl?: string;
}

interface RejectionStats {
  totalRejections: number;
  byReason: { [key: string]: number };
  byTask: { [key: string]: number };
  byCategory: { [key: string]: number };
  byRejectedBy: { [key: string]: number };
  recentRejections: RejectionData[];
}

export default function RejectionAnalytics() {
  const [rejections, setRejections] = useState<RejectionData[]>([]);
  const [stats, setStats] = useState<RejectionStats>({
    totalRejections: 0,
    byReason: {},
    byTask: {},
    byCategory: {},
    byRejectedBy: {},
    recentRejections: []
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    reason: "",
    task: "",
    category: "",
    rejectedBy: "",
    timeRange: "7d" // 7d, 30d, 90d, all
  });
  
  // Image modal state
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);

  useEffect(() => {
    loadRejections();
  }, [filter]);

  const loadRejections = async () => {
    try {
      setLoading(true);
      
      // Calculate time range
      const now = Date.now();
      let timeFilter = 0;
      switch (filter.timeRange) {
        case "7d":
          timeFilter = now - (7 * 24 * 60 * 60 * 1000);
          break;
        case "30d":
          timeFilter = now - (30 * 24 * 60 * 60 * 1000);
          break;
        case "90d":
          timeFilter = now - (90 * 24 * 60 * 60 * 1000);
          break;
        default:
          timeFilter = 0;
      }

      // Build query - simplified to avoid index requirements
      let q = query(
        collection(db, "submissions"),
        where("status", "==", "rejected")
      );

      const snapshot = await getDocs(q);
      const rejectionData: RejectionData[] = [];

      console.log(`Found ${snapshot.size} rejected submissions`);

      snapshot.forEach(doc => {
        const data = doc.data();
        console.log('Processing submission:', doc.id, data);
        
        // Apply time filter in JavaScript
        const rejectedAt = data.rejectedAt || data.adminReviewedAt || 0;
        if (timeFilter > 0 && rejectedAt < timeFilter) {
          return; // Skip if outside time range
        }
        
        // Determine rejection reason - check multiple possible fields
        let reason = data.reason || data.rejectReason || "No reason provided";
        let rejectedBy = data.rejectedBy || data.validatedBy || "Unknown";
        
        // If admin reviewed but status might not be "rejected", check adminReviewedBy
        if (data.adminReviewedBy && !data.rejectedBy && !data.validatedBy) {
          rejectedBy = "admin";
        }
        
        const rejection: RejectionData = {
          id: doc.id,
          taskTitle: data.taskTitle || "Unknown",
          taskCategory: data.category || "Unknown",
          reason: reason,
          rejectedBy: rejectedBy,
          rejectedAt: rejectedAt,
          userId: data.userId || "Unknown",
          similarity: data.validationDetails?.similarity,
          adminReviewedBy: data.adminReviewedBy,
          mediaUrl: data.mediaUrl
        };
        rejectionData.push(rejection);
      });

      // Also check for admin-reviewed submissions that might not have status "rejected"
      const adminReviewedQuery = query(
        collection(db, "submissions"),
        where("adminReviewedBy", "==", "admin")
      );
      
      const adminSnapshot = await getDocs(adminReviewedQuery);
      console.log(`Found ${adminSnapshot.size} admin-reviewed submissions`);
      
      adminSnapshot.forEach(doc => {
        const data = doc.data();
        
        // Apply time filter in JavaScript
        const adminReviewedAt = data.adminReviewedAt || 0;
        if (timeFilter > 0 && adminReviewedAt < timeFilter) {
          return; // Skip if outside time range
        }
        
        // Only add if it's not already in the list and has rejection data
        const alreadyExists = rejectionData.find(r => r.id === doc.id);
        if (!alreadyExists && (data.rejectReason || data.status === "rejected")) {
          console.log('Adding admin-reviewed submission:', doc.id, data);
          
          const rejection: RejectionData = {
            id: doc.id,
            taskTitle: data.taskTitle || "Unknown",
            taskCategory: data.category || "Unknown",
            reason: data.rejectReason || "Admin rejected",
            rejectedBy: "admin",
            rejectedAt: adminReviewedAt,
            userId: data.userId || "Unknown",
            similarity: data.validationDetails?.similarity,
            adminReviewedBy: data.adminReviewedBy,
            mediaUrl: data.mediaUrl
          };
          rejectionData.push(rejection);
        }
      });

      // Sort by rejectedAt in descending order (most recent first)
      rejectionData.sort((a, b) => b.rejectedAt - a.rejectedAt);

      console.log(`Total rejection data: ${rejectionData.length} items`);
      setRejections(rejectionData);
      calculateStats(rejectionData);
    } catch (error) {
      console.error("Error loading rejections:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data: RejectionData[]) => {
    const byReason: { [key: string]: number } = {};
    const byTask: { [key: string]: number } = {};
    const byCategory: { [key: string]: number } = {};
    const byRejectedBy: { [key: string]: number } = {};

    data.forEach(rejection => {
      // Count by reason
      byReason[rejection.reason] = (byReason[rejection.reason] || 0) + 1;
      
      // Count by task
      byTask[rejection.taskTitle] = (byTask[rejection.taskTitle] || 0) + 1;
      
      // Count by category
      byCategory[rejection.taskCategory] = (byCategory[rejection.taskCategory] || 0) + 1;
      
      // Count by who rejected
      byRejectedBy[rejection.rejectedBy] = (byRejectedBy[rejection.rejectedBy] || 0) + 1;
    });

    setStats({
      totalRejections: data.length,
      byReason,
      byTask,
      byCategory,
      byRejectedBy,
      recentRejections: data.slice(0, 10) // Show 10 most recent
    });
  };

  const getFilteredRejections = () => {
    return rejections.filter(rejection => {
      if (filter.reason && !rejection.reason.toLowerCase().includes(filter.reason.toLowerCase())) return false;
      if (filter.task && !rejection.taskTitle.toLowerCase().includes(filter.task.toLowerCase())) return false;
      if (filter.category && rejection.taskCategory !== filter.category) return false;
      if (filter.rejectedBy && rejection.rejectedBy !== filter.rejectedBy) return false;
      return true;
    });
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const getReasonColor = (reason: string) => {
    if (reason.includes("similar")) return "text-blue-600";
    if (reason.includes("match")) return "text-orange-600";
    if (reason.includes("duplicate")) return "text-purple-600";
    if (reason.includes("quality")) return "text-red-600";
    return "text-gray-600";
  };

  const sortedReasons = Object.entries(stats.byReason)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10);

  const sortedTasks = Object.entries(stats.byTask)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10);

  // Image modal handlers
  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Rejection Analytics</h1>
        <p className="text-gray-600">Monitor rejection patterns and identify potential system issues</p>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h2 className="text-lg font-semibold mb-3">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Time Range</label>
            <select
              value={filter.timeRange}
              onChange={(e) => setFilter({...filter, timeRange: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="all">All time</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
            <input
              type="text"
              placeholder="Filter by reason..."
              value={filter.reason}
              onChange={(e) => setFilter({...filter, reason: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Task</label>
            <input
              type="text"
              placeholder="Filter by task..."
              value={filter.task}
              onChange={(e) => setFilter({...filter, task: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={filter.category}
              onChange={(e) => setFilter({...filter, category: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Categories</option>
              {Object.keys(stats.byCategory).map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rejected By</label>
            <select
              value={filter.rejectedBy}
              onChange={(e) => setFilter({...filter, rejectedBy: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="admin">Admin</option>
              <option value="replicate">AI (Replicate)</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading rejection data...</p>
        </div>
      ) : (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-800">Total Rejections</h3>
              <p className="text-3xl font-bold text-red-600">{stats.totalRejections}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-800">Unique Reasons</h3>
              <p className="text-3xl font-bold text-blue-600">{Object.keys(stats.byReason).length}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-800">Tasks Affected</h3>
              <p className="text-3xl font-bold text-green-600">{Object.keys(stats.byTask).length}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-800">Categories</h3>
              <p className="text-3xl font-bold text-purple-600">{Object.keys(stats.byCategory).length}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Top Rejection Reasons */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Rejection Reasons</h3>
              <div className="space-y-3">
                {sortedReasons.map(([reason, count]) => (
                  <div key={reason} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <span className={`text-sm ${getReasonColor(reason)}`}>
                      {reason.length > 50 ? reason.substring(0, 50) + "..." : reason}
                    </span>
                    <span className="font-semibold text-gray-800">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Rejected Tasks */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Most Rejected Tasks</h3>
              <div className="space-y-3">
                {sortedTasks.map(([task, count]) => (
                  <div key={task} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                    <span className="text-sm text-gray-700">{task}</span>
                    <span className="font-semibold text-gray-800">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Rejections Table */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                Recent Rejections ({getFilteredRejections().length} total)
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rejected By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Similarity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getFilteredRejections().slice(0, 20).map((rejection) => (
                    <tr key={rejection.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {rejection.mediaUrl ? (
                          <div 
                            className="w-16 h-16 rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => handleImageClick(rejection.mediaUrl!)}
                          >
                            <img 
                              src={rejection.mediaUrl} 
                              alt="Rejected submission"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                target.parentElement!.innerHTML = '<div class="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs">No Image</div>';
                              }}
                            />
                          </div>
                        ) : (
                          <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center text-gray-500 text-xs">
                            No Image
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{rejection.taskTitle}</div>
                          <div className="text-sm text-gray-500">{rejection.taskCategory}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className={`text-sm ${getReasonColor(rejection.reason)}`}>
                          {rejection.reason.length > 60 ? rejection.reason.substring(0, 60) + "..." : rejection.reason}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          rejection.rejectedBy === 'admin' 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {rejection.rejectedBy}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {rejection.similarity ? `${Math.round(rejection.similarity * 100)}%` : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(rejection.rejectedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={handleCloseImageModal}>
          <div className="relative max-w-4xl max-h-full p-4">
            <button 
              onClick={handleCloseImageModal}
              className="absolute top-2 right-2 text-white text-2xl font-bold hover:text-gray-300 z-10"
            >
              ×
            </button>
            <img 
              src={selectedImage} 
              alt="Rejected submission full view"
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
} 